/**
 * Shared theme/density store.
 *
 * The artifact had three independent writers: component state in the terminal,
 * and two buttons on the Design page that set the <html> attribute directly.
 * That let the swatch preview and the terminal's own label disagree, and the
 * Design buttons never persisted. One store keeps every control in agreement
 * and gives the token layer a single writer.
 */

import { useSyncExternalStore } from 'react';
import {
  applyDensity,
  applyTheme,
  nextDensity,
  readDensity,
  readTheme,
  type Density,
  type Theme,
} from '@/lib/prefs';

let theme: Theme = 'canon';
let density: Density = 'normal';
let initialised = false;

const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function init(): void {
  if (initialised) return;
  initialised = true;
  theme = readTheme();
  density = readDensity();
}

export function subscribe(listener: () => void): () => void {
  init();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getTheme(): Theme {
  init();
  return theme;
}

export function getDensity(): Density {
  init();
  return density;
}

export function setTheme(next: Theme): void {
  init();
  if (theme === next) return;
  theme = next;
  applyTheme(next);
  emit();
}

export function setDensity(next: Density): void {
  init();
  if (density === next) return;
  density = next;
  applyDensity(next);
  emit();
}

export function toggleTheme(): void {
  setTheme(getTheme() === 'canon' ? 'terminal' : 'canon');
}

export function cycleDensity(): void {
  setDensity(nextDensity(getDensity()));
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme, getTheme);
}

export function useDensity(): Density {
  return useSyncExternalStore(subscribe, getDensity, getDensity);
}
